import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SelectInput from '@/components/forms/SelectInput';
import { useController } from 'react-hook-form';
import { useStore } from '@/components/store/Store';
import { FormLabel, Button, Typography } from '@mui/material';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import { noop } from '@/utils/functions';
import M24PageFinder from '@/components/Finder/M24PageFinder/M24PageFinder';
import Styles from '@/assets/js/Styles';
import PageListManualBlockItem from '@/editor/PageEditor/EditorBlock/PageListBlocks/PageListManualBlockItem';
import Container from '../../../../components/Container';
import { BlockComponent } from '../../../lib/declarations/EditorComponentTypes';
import { BlockPath, useCurrentBlock } from '../../CurrentBlockProvider';
import { useControlledFieldArray } from '../../../../hooks/useControlledFieldArray';
import { BlockSpecificPage } from '../../../lib/declarations/BlockSpecificPage';
import { DMManualBlockModel, DMViewType } from '../../../../declarations/models/blocks/DMBlock';
import FinderButton, { FinderType } from '../../../../components/Finder/FinderButton';
import { GenericMedia } from '../../../../declarations/GenericMedia';
import { DMArtifact, DMMediaModel } from '../../../../declarations/models/DMMediaModel';
import { settingsAreaStyle } from '../EmployeeBlocks/EmployeesAutoBlock';
import DMBlockItem from './DMBlockItem';
import { DMObjectType } from '../../../../declarations/models/DMObjectType';
import TextInput from '../../../../components/forms/TextInput';
import SwitchInput from '../../../../components/forms/SwitchInput';
import { Api } from '../../../../services/Api';
import SingleDMItem from './SingleDMItem';
import { OutlinedContainer } from '../../../../components/OutlinedContainer';

export const DMManualBlock: BlockComponent = () => {
  const { t } = useTranslation('components');
  const { blockPath, block } = useCurrentBlock();
  const currentBlock = block as DMManualBlockModel;
  const [pageFinderOpen, setPageFinderOpen] = useState<boolean>(false);
  const {
    state: { currentUser },
  } = useStore();
  const isSuperAdmin = currentUser?.user?.is_super_admin;
  const [museumArtifact, setMuseumArtifact] = useState<DMArtifact>();

  const gridSizeOptions = ['auto', '3', '4', '5', '6'];
  const mobileGridOptions = ['auto', 'horizontal'];

  const {
    fields: dmItems,
    append: appendDm,
    insert: insertDm,
    move: moveDm,
    remove: removeDm,
  } = useControlledFieldArray<BlockSpecificPage<DMManualBlockModel>, `${BlockPath}.dmItems`, 'itemHash'>({
    name: `${blockPath}.dmItems`,
  });

  const {
    field: { value: museum, onChange: setMuseum },
  } = useController<BlockSpecificPage<DMManualBlockModel>, `${typeof blockPath}.museum`>({
    name: `${blockPath}.museum`,
  });

  const handleAddDmItem = (item: DMMediaModel | null) => {
    if (item?.['artifact.uniqueId'])
      appendDm({ uniqueId: item['artifact.uniqueId'], title: item['artifact.ingress.title'] });
  };

  const handleSetMuseum = (item: DMMediaModel | null) => {
    if (item?.['artifact.uniqueId'])
      setMuseum({ uniqueId: item['artifact.uniqueId'], title: item['artifact.ingress.title'] });
  };

  const {
    field: { value: showAllLinkPage, onChange: onChangeShowAllLinkPage },
  } = useController<BlockSpecificPage<DMManualBlockModel>, `${BlockPath}.showAllLinkPage`>({
    name: `${blockPath}.showAllLinkPage`,
  });

  useEffect(() => {
    if (museum) {
      if (museum.uniqueId) {
        const ctx = Api.getDMArtifact(museum.uniqueId);

        ctx
          .fetchDirect(null)
          .then((res) => !!res && setMuseumArtifact(res))
          .finally(ctx.abort);
      }
    } else setMuseumArtifact(undefined);
  }, [museum]);

  return (
    <Container gap={2} column top left fullWidth>
      <M24PageFinder
        hideSiteSelector
        open={pageFinderOpen}
        onClose={() => setPageFinderOpen(false)}
        onSelectionConfirmed={(pages) => {
          if (pages.length > 0) {
            onChangeShowAllLinkPage(pages[0].source);
          }
        }}
      />
      <Container column gap={2} fullWidth sx={settingsAreaStyle}>
        <FinderButton
          type={FinderType.DM}
          disabled
          multiSelect={false}
          overrideText={t('DMBlock.DMMuseumLabel')}
          overrideIcon={<MaterialSymbol name='museum' />}
          finderProps={{
            onSelectionConfirmed: (items: Array<GenericMedia<DMMediaModel>>) =>
              !!items.length && handleSetMuseum(items[0].source),
            objectType: DMObjectType.MUSEUM,
          }}
        />
        {!!museumArtifact && (
          <>
            <SingleDMItem dmArtifact={museumArtifact} onRemove={() => setMuseum(null)} />
            <Container left column fullWidth>
              <FormLabel>{t('DMBlock.AlternativeFields')}:</FormLabel>
              <Container column gap={2} fullWidth>
                <TextInput path={`${blockPath}.museumData.altTitle`} label={t('DMBlock.AltTitleLabel')} size='small' />
                <TextInput
                  path={`${blockPath}.museumData.altDescription`}
                  label={t('DMBlock.AltDescriptionLabel')}
                  multiline
                  minRows={1}
                  maxRows={8}
                  size='small'
                />
                {/* <SwitchInput path={`${blockPath}.museumData.wideDisplay`} label={t('DMBlock.WideDisplayLabel')} /> */}
              </Container>
            </Container>
          </>
        )}
      </Container>
      <Container column fullWidth sx={settingsAreaStyle} mb={3}>
        <FinderButton
          type={FinderType.DM}
          overrideText={t('DMBlock.DMItemsLabel')}
          finderProps={{
            onSelectionConfirmed: (items: Array<GenericMedia<DMMediaModel>>) =>
              items.forEach((item) => handleAddDmItem(item.source)),
            hasLocationDep: currentBlock?.view === DMViewType.MAP,
          }}
        />
        {!!dmItems.length && (
          <Container column fullWidth gap={0}>
            {dmItems.map((item, i) => (
              <DMBlockItem
                key={item.uniqueId}
                index={i}
                onReorder={moveDm}
                onAdd={(itemToAdd, addAtIndex) => itemToAdd && insertDm(addAtIndex, itemToAdd)}
                onRemove={removeDm}
              />
            ))}
          </Container>
        )}
      </Container>
      <OutlinedContainer fullWidth column left label={t('KPBlock.DisplayOptionsModuleLabel')}>
        <Container fullWidth left pt={1} wrap gap={2}>
          {currentBlock?.view === DMViewType.GRID && (
            <div style={{ width: '150px' }}>
              <SelectInput
                hideNoSelectionOption
                defaultValue='auto'
                options={gridSizeOptions}
                getOptionKey={(gridSize) => `size-${gridSize}`}
                getOptionLabel={(gridSize) => `${gridSize}`}
                path={`${blockPath}.gridSize`}
                label={t('CardModuleBlock.GridSizeLabel')}
              />
            </div>
          )}
          {currentBlock?.view === DMViewType.GRID && (
            <div style={{ width: '250px' }}>
              <SelectInput
                hideNoSelectionOption
                defaultValue='auto'
                options={mobileGridOptions}
                getOptionKey={(o) => `mobileGrid-${o}`}
                getOptionLabel={(o) => t(`CardModuleViewTypeSelector.CardModuleViewTypeVariant.${o}`)}
                path={`${blockPath}.gridMobileBehaviour`}
                label={t('CardModuleBlock.GridMobileBehaviourLabel')}
              />
            </div>
          )}

          <SwitchInput
            fullWidth={false}
            path={`${blockPath}.showInSameTab`}
            label={t('DMFilterSearch.ShowInSameTab')}
          />
          {isSuperAdmin && (
            <Container column left sx={{ width: '300px' }}>
              <div className='superadmin' style={{ backgroundColor: '#fbe6ff', padding: '1em', width: '100%' }}>
                <SwitchInput path={`${blockPath}.integratedView`} label={t('KPBlock.IntegratedView')} />
              </div>
            </Container>
          )}
        </Container>
      </OutlinedContainer>
      {currentBlock?.view !== DMViewType.MAP && (
        <OutlinedContainer fullWidth column left label={t('DMBlock.DmLinkLabel')}>
          <Container fullWidth left pt={1} gap={2}>
            <TextInput path={`${blockPath}.dmLink`} defaultValue='' label={t('DMBlock.DmLink')} size='small' />
            <TextInput path={`${blockPath}.dmLinkText`} defaultValue='' label={t('DMBlock.DmLinkText')} size='small' />
          </Container>
        </OutlinedContainer>
      )}
      {currentBlock?.view !== DMViewType.MAP && (
        <OutlinedContainer fullWidth column left label={t('EventsBlock.ShowAllLinkPageTitle')}>
          {showAllLinkPage && (
            <PageListManualBlockItem
              index={0}
              onReorder={noop}
              onDelete={() => onChangeShowAllLinkPage(null)}
              overrideItemPath={`${blockPath}.showAllLinkPage`}
              color={Styles.Colors.LIGHT_GREY}
              hideHighlightCheckbox
              hideSkinSelector
              disableReordering
            />
          )}
          <Button onClick={() => setPageFinderOpen(true)} variant='outlined' color='secondary'>
            {t('EventsBlock.ShowAllLinkPageButtonLabel')}
          </Button>
        </OutlinedContainer>
      )}
    </Container>
  );
};

export default DMManualBlock;
